type HybrisDateRange = { olderThanTS: string; newerThanTS: string };

export type OrderState = {
  loading: boolean;

  onlineOrders: null | any;
  offlineOrders: null | any;

  orderAmount: number;
  orderAmountOnline: number;
  orderAmountOffline: number;

  showOnlineOrders: boolean;
  showAllOrders: boolean;

  filterTimespan: string | 'last_6_months';
};

export const useOrderStore = defineStore('order', {
  state: (): OrderState => ({
    loading: false,
    onlineOrders: null,
    offlineOrders: null,
    orderAmount: 0,
    orderAmountOnline: 0,
    orderAmountOffline: 0,
    showOnlineOrders: false,
    showAllOrders: true,
    filterTimespan: 'last_6_months',
  }),
  getters: {
    getOnlineOrders(state: any) {
      return [...state.onlineOrders?.crmOrders, ...state.onlineOrders?.shopOrders];
    },
    getOfflineOrders(state: any) {
      return state.offlineOrders?.receipts?.map((order: any) => ({
        ...order,
        created: order.receiptHeader.orderDate,
        offline: true,
      }));
    },
    getAllOrders(_state: any) {
      return [...this.getOfflineOrders, ...this.getOnlineOrders].sort(
        (a, b) => new Date(b?.created).getTime() - new Date(a?.created).getTime(),
      );
    },
    getOrdersFetchDate(state: any) {
      if (state.filterTimespan === LAST_SIX_MONTHS) {
        return getLastMonths(6);
      }

      return {
        newerThanTS: `${state.filterTimespan}-01-01T00:00:00+0000`,
        olderThanTS: `${state.filterTimespan}-12-31T23:59:59+0000`,
      };
    },
  },
  actions: {
    async fetchOnlineOrders(date: HybrisDateRange, skipFinishLoading: boolean) {
      const { $api, $errorHandler } = useNuxtApp();
      const mainStore = useMainStore();
      const userStore = useUserStore();

      logger.debug('Start fetching online order history...');
      this.startLoading();

      return $api.hybris.order
        .orderHistory(userStore.getType, 'FULL', date.olderThanTS, date.newerThanTS, mainStore.language)
        .then((response: any) => {
          logger.debug('Online order data fetched.');
          this.orderAmountOnline = response.data?.crmOrders?.length + response.data?.shopOrders?.length;
          this.onlineOrders = response.data;
        })
        .catch((error: any) => {
          logger.error('Error while fetching online order data.', error);

          if (!$errorHandler.isErrorType(error, CUSTOMER_NOT_SUFFICIENT_ERROR)) {
            $errorHandler.handleErrorModal(error);
          }
        })
        .finally(() => {
          if (!skipFinishLoading) {
            this.finishLoading();
          }
        });
    },

    async fetchDetailedOnlineOrder(orderId: string) {
      const { $api, $errorHandler } = useNuxtApp();
      const mainStore = useMainStore();
      const userStore = useUserStore();

      logger.debug('Start fetching detailed online order...');
      this.startLoading();

      return $api.hybris.order
        .singleOrder(userStore.getType, orderId, mainStore.language)
        .then((response: any) => {
          logger.debug('Successfully fetched detailed online order.');
          return response.data;
        })
        .catch((error: any) => {
          logger.error('Error while fetchting detailed online order data.', error);

          if (!$errorHandler.isErrorType(error, CUSTOMER_NOT_SUFFICIENT_ERROR)) {
            $errorHandler.handleErrorModal(error);
          }
        })
        .finally(() => {
          this.finishLoading();
        });
    },

    async fetchOfflineOrders(date: HybrisDateRange, skipFinishLoading: boolean) {
      const { $api, $errorHandler } = useNuxtApp();
      const mainStore = useMainStore();
      const userStore = useUserStore();

      logger.debug('Start fetching offline order history...');
      this.startLoading();

      return $api.hybris.order
        .receiptHistory(userStore.getType, 'FULL', date.olderThanTS, date.newerThanTS, mainStore.language)
        .then((response: any) => {
          logger.debug('Offline order data fetched.');
          this.orderAmountOffline = response.data?.receipts?.length;
          this.offlineOrders = response.data;
        })
        .catch((error: any) => {
          logger.error('Error while fetchting offline order data.', error);

          if (!$errorHandler.isErrorType(error, CUSTOMER_NOT_SUFFICIENT_ERROR)) {
            $errorHandler.handleErrorModal(error);
          }
        })
        .finally(() => {
          if (!skipFinishLoading) {
            this.finishLoading();
          }
        });
    },

    async fetchDetailedOfflineOrder(payload: {
      receiptCode: string;
      orderDateShort: string;
      storeNumber: string;
      workstationId: string;
    }) {
      const { $api, $errorHandler } = useNuxtApp();
      const mainStore = useMainStore();
      const userStore = useUserStore();

      logger.debug('Start fetching detailed offline order...');
      this.startLoading();

      return $api.hybris.order
        .singleReceipt(
          userStore.getType,
          payload.receiptCode,
          payload.orderDateShort,
          payload.storeNumber,
          payload.workstationId,
          mainStore.language,
        )
        .then((response: any) => {
          logger.debug('Successfully fetched detailed offline order.');
          return response.data;
        })
        .catch((error: any) => {
          logger.error('Error while fetchting detailed offline order data.', error);

          if (!$errorHandler.isErrorType(error, CUSTOMER_NOT_SUFFICIENT_ERROR)) {
            $errorHandler.handleErrorModal(error);
          }
        })
        .finally(() => this.finishLoading());
    },

    async fetchAllOrders(date: HybrisDateRange) {
      await Promise.all([this.fetchOnlineOrders(date, true), this.fetchOfflineOrders(date, true)]);
      this.finishLoading();
    },

    async fetchReturnOrderDetails(orderId: string, email: string, businessOrderDay: string) {
      const { $api } = useNuxtApp();

      logger.debug('Start fetching return order details...');
      this.startLoading();

      const apiRequestDate = formatDateStringToYYYYMMDD(businessOrderDay);

      return $api.hybris.order
        .getDigitalReturn(orderId, email, apiRequestDate)
        .then((response: any) => {
          logger.debug('Successfully fetched return order details.');
          return response;
        })
        .catch((error: any) => {
          logger.error('Error while fetching return order details.');
          throw error;
        })
        .finally(() => this.finishLoading());
    },

    async orderReturn(orderId: string, lineItems: Record<string, any>) {
      const { $api } = useNuxtApp();

      logger.debug('Start creating order return...');
      this.startLoading();

      return $api.hybris.order
        .orderReturn(orderId, lineItems)
        .then(() => {
          logger.debug('Successfully created order return.');
        })
        .catch((error: any) => {
          logger.error('Error while creating order return.');
          throw error;
        })
        .finally(() => this.finishLoading());
    },

    startLoading() {
      this.loading = true;
    },

    finishLoading() {
      this.loading = false;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderStore, import.meta.hot));
}
